import './settings.css';
import { Button, DatePicker, Form, Input, InputNumber, Select, Tag, Checkbox, ColorPicker, Tabs, Popconfirm, Tooltip, Upload, message } from 'antd';
import {
    ApartmentOutlined, ApiOutlined, BankOutlined, CheckCircleOutlined, CopyOutlined,
    DeleteOutlined, FormOutlined, MessageOutlined, ProjectOutlined, RobotOutlined,
    TagsOutlined, WalletOutlined,
} from '@ant-design/icons';
import { cloneElement, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import CompanyForm from '../companies/company-form';
import CrudTable from '@controls/crud-table/crud-table';
import FilterBar from '@controls/filter-bar/filter-bar';
import { serverFetch, fileUrl } from '@server';
import { showError, fmtDate, toDayjs, toApiDate } from '@extensions/utils';
import { useDicts, CHANNEL_VIDS, CHANNEL_BEELINE, CHANNEL_MAX_WAPPI, CHANNEL_TG_WAPPI, channelVidName } from '@extensions/dicts';
import { VERSION_RE } from '../tasks/task-version';
import AiSettings from './ai-settings';

const Company = () => {
    const [item, setItem] = useState(null);

    useEffect(() => {
        serverFetch('company/current')
            .then(setItem)
            .catch(showError);
    }, []);

    return (
        <CompanyForm
            title="Компания"
            item={item}
            onSave={(v) => serverFetch('company/current', { method: 'PUT', bodyData: v }).then((c) => { setItem(c); return c; })}
        />
    );
};

const colorRender = (color) => color ? <Tag color={color}>{color}</Tag> : '';

const tagPreview = (record) => (
    <Tag color={record.bgColor || undefined} style={{ color: record.textColor || undefined }}>
        {record.name}
    </Tag>
);

const SETTINGS_TABS = [
    { key: 'company', label: 'Компания', icon: <BankOutlined />, adminOnly: true },
    { key: 'departments', label: 'Отделы', icon: <ApartmentOutlined /> },
    { key: 'statuses', label: 'Статусы', icon: <CheckCircleOutlined /> },
    { key: 'channels', label: 'Каналы', icon: <ApiOutlined />, adminOnly: true },
    { key: 'ai', label: 'Нейросети', icon: <RobotOutlined />, adminOnly: true },
    { key: 'patterns', label: 'Шаблоны', icon: <MessageOutlined /> },
    { key: 'tags', label: 'Метки', icon: <TagsOutlined /> },
    { key: 'projects', label: 'Проекты', icon: <ProjectOutlined /> },
    { key: 'fields', label: 'Доп. поля', icon: <FormOutlined /> },
    { key: 'cashboxes', label: 'Кассы', icon: <WalletOutlined /> },
];

const Settings = () => {
    const user = useSelector((s) => s.user);
    const { page } = useParams();
    const navigate = useNavigate();
    const tabs = SETTINGS_TABS.filter((tab) => !tab.adminOnly || user?.role === 0);
    const activeKey = tabs.some((tab) => tab.key === page) ? page : tabs[0].key;

    if (!user || user.role > 10) return <Navigate to="/tasks" replace />;
    if (page === 'users') return <Navigate to="/users" replace />;
    if (page === 'backup') return <Navigate to="/service" replace />;
    if (page === 'products' || page === 'product-groups') return <Navigate to="/products" replace />;
    if (page === 'blocks' || page === 'versions') return <Navigate to="/settings/projects" replace />;
    if (page === 'order-statuses') return <Navigate to="/settings/statuses" replace />;

    return (
        <div>
            <Tabs
                className="settings-tabs"
                activeKey={activeKey}
                onChange={(key) => navigate(`/settings/${key}`)}
                destroyOnHidden
                items={tabs.map(({ key, label, icon }) => ({
                    key,
                    label,
                    icon,
                    children: key === activeKey ? <SettingsSection page={key} /> : null,
                }))}
            />
        </div>
    );
};

const ProjectParts = ({ projectId, onChanged }) => (
    <Tabs
        className="project-card-tabs"
        items={[
            {
                key: 'main',
                label: 'Проект',
                children: (
                    <>
                        <Form.Item name="name" label="Наименование" rules={[{ required: true }]}><Input /></Form.Item>
                        <Form.Item name="comment" label="Комментарий"><Input /></Form.Item>
                        <Form.Item name="color" label="Цвет" getValueFromEvent={(c) => typeof c === 'string' ? c : c?.toHexString?.() ?? ''}>
                            <ColorPicker showText />
                        </Form.Item>
                        <Form.Item name="isClosed" label="Закрыт" valuePropName="checked"><Checkbox /></Form.Item>
                    </>
                ),
            },
            {
                key: 'blocks',
                label: 'Блоки',
                children: projectId ? (
                    <CrudTable
                        api="block"
                        queryParams={{ projectId }}
                        onChanged={onChanged}
                        columns={[
                            { title: 'Наименование', dataIndex: 'name' },
                            { title: 'Порядок', dataIndex: 'idx', width: 80 },
                        ]}
                        mapFromForm={(values, editing) => ({
                            ...(editing ?? {}), ...values, projectId, idx: values.idx ?? 0,
                        })}
                        renderForm={() => (
                            <>
                                <Form.Item name="name" label="Наименование" rules={[{ required: true }]}><Input /></Form.Item>
                                <Form.Item name="idx" label="Порядок"><InputNumber min={0} /></Form.Item>
                            </>
                        )}
                    />
                ) : <div className="settings-hint">Сохраните проект, чтобы добавить блоки.</div>,
            },
            {
                key: 'versions',
                label: 'Версии',
                children: projectId ? (
                    <CrudTable
                        api="version"
                        queryParams={{ projectId }}
                        onChanged={onChanged}
                        columns={[
                            { title: 'Версия', dataIndex: 'name' },
                            { title: 'Дата выпуска', dataIndex: 'releaseDate', render: fmtDate },
                            { title: 'Комментарий', dataIndex: 'comment' },
                        ]}
                        mapToForm={(record) => ({ ...record, releaseDate: toDayjs(record.releaseDate) })}
                        mapFromForm={(values, editing) => ({
                            ...(editing ?? {}), ...values, projectId, releaseDate: toApiDate(values.releaseDate),
                        })}
                        renderForm={() => (
                            <>
                                <Form.Item name="name" label="Версия" rules={[
                                    { required: true },
                                    { pattern: VERSION_RE, message: 'Формат: 5.4.4.50' },
                                ]}>
                                    <Input />
                                </Form.Item>
                                <Form.Item name="releaseDate" label="Дата выпуска">
                                    <DatePicker format="DD.MM.YYYY" />
                                </Form.Item>
                                <Form.Item name="comment" label="Комментарий"><Input /></Form.Item>
                            </>
                        )}
                    />
                ) : <div className="settings-hint">Сохраните проект, чтобы добавить версии.</div>,
            },
        ]}
    />
);

const statusColumns = [
    { title: 'Наименование', dataIndex: 'name' },
    { title: 'Порядок', dataIndex: 'idx', width: 80 },
    { title: 'Цвет', dataIndex: 'color', render: colorRender },
];

const StatusForm = () => (
    <>
        <Form.Item name="name" label="Наименование" rules={[{ required: true }]}><Input /></Form.Item>
        <Form.Item name="idx" label="Порядок"><InputNumber min={0} /></Form.Item>
        <Form.Item name="color" label="Цвет" getValueFromEvent={(c) => typeof c === 'string' ? c : c?.toHexString?.() ?? ''}>
            <ColorPicker showText />
        </Form.Item>
    </>
);

const STATUS_VIDS = [
    { value: 4, label: 'Задачи' },
    { value: 5, label: 'Заказы' },
];

const StatusesPage = ({ onChanged }) => {
    const [vid, setVid] = useState(4);
    return (
        <CrudTable
            title="Статусы"
            api="status"
            queryParams={{ vid }}
            onChanged={onChanged}
            columns={statusColumns}
            extraButtons={(
                <Select
                    value={vid}
                    onChange={setVid}
                    options={STATUS_VIDS}
                    style={{ width: 160 }}
                    aria-label="Вид статусов"
                />
            )}
            mapFromForm={(values, editing) => ({ ...(editing ?? {}), ...values, vid })}
            renderForm={() => <StatusForm />}
        />
    );
};

const SettingsSection = ({ page }) => {
    const { reloadDicts } = useDicts();

    const wrap = (node) => cloneElement(node, { onChanged: reloadDicts });

    switch (page) {
        case 'company':
            return <Company />;
        case 'departments':
            return wrap(
                <CrudTable
                    title="Отделы"
                    api="department"
                    columns={[
                        { title: 'Наименование', dataIndex: 'name' },
                        { title: 'Комментарий', dataIndex: 'comment' },
                        { title: 'Начало', dataIndex: 'startHour' },
                        { title: 'Окончание', dataIndex: 'finishHour' },
                    ]}
                    renderForm={() => (
                        <>
                            <Form.Item name="name" label="Наименование" rules={[{ required: true }]}><Input /></Form.Item>
                            <Form.Item name="comment" label="Комментарий"><Input /></Form.Item>
                            <Form.Item name="startHour" label="Час начала"><InputNumber min={0} max={23} /></Form.Item>
                            <Form.Item name="finishHour" label="Час окончания"><InputNumber min={0} max={23} /></Form.Item>
                        </>
                    )}
                />
            );
        case 'statuses':
            return <StatusesPage onChanged={reloadDicts} />;
        case 'channels':
            return wrap(<ChannelsPage />);
        case 'ai':
            return <AiSettings />;
        case 'patterns':
            return (
                <CrudTable
                    title="Шаблоны"
                    api="messagepattern"
                    columns={[
                        { title: 'Наименование', dataIndex: 'name' },
                        { title: 'Текст', dataIndex: 'body' },
                        { title: 'Порядок', dataIndex: 'idx', width: 80 },
                        { title: 'Активен', dataIndex: 'active', width: 90, render: (v) => (v ? 'да' : 'нет') },
                    ]}
                    toolbar={
                        <div className="card-list-muted" style={{ padding: '4px 0' }}>
                            Подстановки: {'{name}'} — имя клиента, {'{phone}'}, {'{email}'}, {'{company}'} — фирма, {'{user}'} — оператор
                        </div>
                    }
                    mapFromForm={(values, editing) => ({ ...(editing ?? {}), ...values, active: values.active ?? false })}
                    renderForm={() => (
                        <>
                            <Form.Item name="name" label="Наименование" rules={[{ required: true }]}><Input /></Form.Item>
                            <Form.Item name="body" label="Текст" rules={[{ required: true }]}>
                                <Input.TextArea autoSize={{ minRows: 3, maxRows: 10 }} />
                            </Form.Item>
                            <Form.Item name="idx" label="Порядок"><InputNumber min={0} /></Form.Item>
                            <Form.Item name="active" label="Активен" valuePropName="checked" initialValue={true}><Checkbox /></Form.Item>
                        </>
                    )}
                />
            );
        case 'tags':
            return wrap(<TagsPage />);
        case 'projects':
            return wrap(
                <CrudTable
                    title="Проекты"
                    api="project"
                    drawer
                    modalWidth={760}
                    columns={[
                        { title: 'Наименование', dataIndex: 'name' },
                        { title: 'Комментарий', dataIndex: 'comment' },
                        { title: 'Цвет', dataIndex: 'color', render: colorRender },
                        { title: 'Закрыт', dataIndex: 'isClosed', render: (v) => (v ? 'да' : 'нет') },
                    ]}
                    renderForm={(_form, editing) => (
                        <ProjectParts projectId={editing?.id} onChanged={reloadDicts} />
                    )}
                    mapFromForm={(values, editing) => ({ ...(editing ?? {}), ...values, isClosed: values.isClosed ?? false })}
                />
            );
        case 'cashboxes':
            return wrap(
                <CrudTable
                    title="Кассы"
                    api="cashbox"
                    columns={[
                        { title: 'Наименование', dataIndex: 'name' },
                        { title: 'Порядок', dataIndex: 'idx', width: 80 },
                    ]}
                    renderForm={() => (
                        <>
                            <Form.Item name="name" label="Наименование" rules={[{ required: true }]}><Input /></Form.Item>
                            <Form.Item name="idx" label="Порядок"><InputNumber min={0} /></Form.Item>
                        </>
                    )}
                />
            );
        case 'fields':
            return wrap(
                <CrudTable
                    title="Доп. поля контрагентов"
                    api="field"
                    queryParams={{ vid: 1 }}
                    columns={[
                        { title: 'Наименование', dataIndex: 'name' },
                        { title: 'Вид данных', dataIndex: 'kind' },
                    ]}
                    mapFromForm={(values, editing) => ({ ...(editing ?? {}), ...values, vid: 1 })}
                    renderForm={() => (
                        <>
                            <Form.Item name="name" label="Наименование" rules={[{ required: true }]}><Input /></Form.Item>
                            <Form.Item name="kind" label="Вид данных">
                                <Select options={[{ value: 0, label: 'Строка' }, { value: 1, label: 'Число' }, { value: 2, label: 'Дата' }]} />
                            </Form.Item>
                        </>
                    )}
                />
            );
        default:
            return null;
    }
};

const CHANNEL_SITE = 201;

const copyText = (text) => {
    navigator.clipboard?.writeText(text ?? '')
        .then(() => message.success('Скопировано'))
        .catch(() => message.error('Не удалось скопировать'));
};

// Поля формы канала: для Site код генерируется на сервере и не меняется
const ChannelFields = () => {
    const form = Form.useFormInstance();
    const vid = Form.useWatch('vid');
    const code = Form.useWatch('code', { preserve: true });
    const script = Form.useWatch('script', { preserve: true });
    const id = Form.useWatch('id');
    const iconFileId = Form.useWatch('iconFileId');
    const isSite = vid === CHANNEL_SITE;

    const uploadIcon = (file) => {
        const fd = new FormData();
        fd.append('file', file);
        serverFetch(`channel/${id}/icon`, { method: 'POST', formData: fd })
            .then((result) => form.setFieldsValue({ iconFileId: result.iconFileId }))
            .catch(showError);
        return false;
    };

    const deleteIcon = () => {
        serverFetch(`channel/${id}/icon`, { method: 'DELETE' })
            .then(() => form.setFieldsValue({ iconFileId: null }))
            .catch(showError);
    };

    const hints = {
        1: 'Токен бота из BotFather',
        3: 'Токен бота Max',
        5: 'Ключ доступа сообщества с правами на сообщения и управление',
        [CHANNEL_MAX_WAPPI]: 'Токен API:ID профиля',
        [CHANNEL_TG_WAPPI]: 'Токен API:ID профиля',
        [CHANNEL_BEELINE]: 'Токен API',
        [CHANNEL_SITE]: 'Код генерируется автоматически',
    };

    return (
        <>
            <Form.Item name="active" label="Активен" valuePropName="checked" initialValue={true}><Checkbox /></Form.Item>
            <Form.Item name="vid" label="Вид" rules={[{ required: true }]}
                extra={hints[vid]}>
                <Select options={CHANNEL_VIDS} disabled={isSite && !!code} />
            </Form.Item>
            <Form.Item name="name" label="Наименование" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item label="Иконка" extra={id ? 'PNG, JPG или SVG' : 'Сохраните канал, чтобы загрузить иконку'}>
                {iconFileId ? (
                    <img className="channel-icon" src={fileUrl(iconFileId, true)} alt="" style={{ width: 32, height: 32, display: 'block', marginBottom: 8 }} />
                ) : null}
                {id ? (
                    <>
                        <Upload showUploadList={false} accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml" beforeUpload={uploadIcon}>
                            <Button size="small">{iconFileId ? 'Заменить' : 'Загрузить'}</Button>
                        </Upload>
                        {iconFileId ? (
                            <Button size="small" danger style={{ marginLeft: 8 }} onClick={deleteIcon}>Удалить</Button>
                        ) : null}
                    </>
                ) : null}
            </Form.Item>
            {isSite ? (
                <>
                    {code && (
                        <Form.Item name="code" label="Код (токен виджета)">
                            <Input
                                readOnly
                                suffix={<Button type="text" size="small" icon={<CopyOutlined />} onClick={() => copyText(code)} />}
                            />
                        </Form.Item>
                    )}
                    {script && (
                        <Form.Item label="Код вставки на сайт" extra="Вставьте перед закрывающим тегом </body>">
                            <Input.TextArea readOnly value={script} autoSize={{ minRows: 2, maxRows: 4 }} />
                        </Form.Item>
                    )}
                    {script && (
                        <Button size="small" icon={<CopyOutlined />} onClick={() => copyText(script)}>Копировать скрипт</Button>
                    )}
                    {code && !script && (
                        <Form.Item label="Код вставки на сайт">
                            Не задан Connections:WidgetScriptUrl в настройках сервера
                        </Form.Item>
                    )}
                    {!id && (
                        <Form.Item label=" ">
                            Код и скрипт появятся после сохранения
                        </Form.Item>
                    )}
                </>
            ) : (
                <Form.Item name="code" label="Код/токен" rules={vid === CHANNEL_MAX_WAPPI || vid === CHANNEL_TG_WAPPI ? [{
                    validator: (_, value) => {
                        const text = (value ?? '').trim();
                        if (!text || /^\s*[^:\s]+\s*:\s*[^:\s]+\s*$/.test(text))
                            return Promise.resolve();
                        return Promise.reject(new Error('Формат: Токен API:ID профиля'));
                    },
                }] : undefined}>
                    <Input placeholder={vid === CHANNEL_MAX_WAPPI || vid === CHANNEL_TG_WAPPI ? 'Токен API:ID профиля' : undefined} />
                </Form.Item>
            )}
            <Form.Item name="url" label="Ссылка"><Input /></Form.Item>
            {vid === CHANNEL_BEELINE && (
                <Form.Item
                    name="incomingLines"
                    label="Входящие линии"
                    extra="Номера через запятую, которые показывать в диалогах. Пусто — без фильтра."
                >
                    <Input placeholder="74951234567, 74957654321" />
                </Form.Item>
            )}
            <Form.Item name="idx" label="Порядок"><InputNumber min={0} /></Form.Item>
            <Form.Item name="id" hidden><Input /></Form.Item>
            {vid === CHANNEL_BEELINE && <BeelineAbonents channelId={id} />}
        </>
    );
};

// Абоненты АТС и кто из сотрудников им соответствует
const BeelineAbonents = ({ channelId }) => {
    const { dicts } = useDicts();
    const [rows, setRows] = useState([]);

    const load = useCallback(() => {
        if (!channelId) return;
        serverFetch(`channel/${channelId}/abonents`).then(setRows).catch(showError);
    }, [channelId]);

    useEffect(() => { load(); }, [load]);

    if (!channelId) return null;

    const setUser = (abonentId, userId) => {
        serverFetch(`channel/${channelId}/abonents/${abonentId}`, {
            method: 'PUT',
            bodyData: { userId: userId ?? null },
        }).then(setRows).catch(showError);
    };

    return (
        <div>
            <div className="task-section-title">Абоненты</div>
            {rows.length === 0 && <div className="task-empty">Абонентов пока нет</div>}
            {rows.map((row) => (
                <div key={row.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                        {row.name}{row.extension ? ` · ${row.extension}` : ''}{row.phone ? ` · ${row.phone}` : ''}
                    </span>
                    <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="Сотрудник"
                        style={{ width: 180 }}
                        value={row.userId ?? undefined}
                        options={dicts.users.map((u) => ({ value: u.id, label: u.name }))}
                        onChange={(value) => setUser(row.id, value)}
                    />
                </div>
            ))}
        </div>
    );
};

const ChannelsPage = () => (
    <CrudTable
        title="Каналы связи"
        api="channel"
        columns={[
            { title: 'Активен', dataIndex: 'active', width: 90, render: (v) => (v ? 'да' : 'нет') },
            {
                title: '', dataIndex: 'iconFileId', width: 40,
                render: (fileId) => (fileId ? <img className="channel-icon" src={fileUrl(fileId, true)} alt="" /> : null),
            },
            { title: 'Вид', dataIndex: 'vid', render: channelVidName },
            { title: 'Наименование', dataIndex: 'name' },
            { title: 'Код/токен', dataIndex: 'code' },
            { title: 'Ссылка', dataIndex: 'url' },
        ]}
        mapFromForm={(values, editing) => {
            const body = { ...(editing ?? {}), ...values, active: values.active ?? false };
            delete body.id;
            if (editing?.vid === CHANNEL_SITE) body.code = editing.code; // код сайта не меняется
            return body;
        }}
        renderForm={() => <ChannelFields />}
    />
);

const TagsPage = ({ onChanged }) => {
    const [selected, setSelected] = useState([]);
    const [reloadKey, setReloadKey] = useState(0);
    const [filters, setFilters] = useState({ vid: 0, q: '' });
    const [rows, setRows] = useState([]);

    const convert = () => {
        serverFetch('tag/convert-to-projects', { method: 'POST', bodyData: { tagIds: selected } })
            .then((r) => {
                message.success(`Проектов создано: ${r.projects}, задач привязано: ${r.tasksUpdated}, скопировано: ${r.tasksCopied}`);
                setSelected([]);
                setReloadKey((k) => k + 1);
                onChanged?.();
            })
            .catch(showError);
    };

    const removeSelected = () => {
        Promise.all(selected.map((id) => serverFetch(`tag/${id}`, { method: 'DELETE' })))
            .then(() => {
                message.success(`Удалено меток: ${selected.length}`);
                setSelected([]);
                setReloadKey((k) => k + 1);
                onChanged?.();
            })
            .catch((e) => {
                showError(e);
                setReloadKey((k) => k + 1);
            });
    };

    const onLoaded = (items) => {
        setRows(items);
        setSelected((s) => s.filter((id) => items.some((i) => i.id === id)));
    };

    const hasNonTask = selected.some((id) => rows.find((r) => r.id === id)?.vid !== 4);
    const convertDisabled = !selected.length || hasNonTask;
    const convertButton = (
        <Popconfirm
            title="Конвертировать выбранные метки в проекты? Метки будут удалены."
            okText="Да"
            cancelText="Нет"
            onConfirm={convert}
        >
            <Button disabled={convertDisabled}>Конвертировать в проекты</Button>
        </Popconfirm>
    );

    return (
        <CrudTable
            title="Метки"
            api="tag"
            rowSelection={{ selectedRowKeys: selected, onChange: setSelected }}
            reloadKey={reloadKey}
            infinite={30}
            filterItems={(items) => items.filter((t) =>
                (!filters.vid || t.vid === filters.vid)
                && (!filters.q || t.name.toLowerCase().includes(filters.q.toLowerCase())))}
            toolbar={
                <FilterBar
                    filters={[{
                        key: 'vid', label: 'Вид', emptyValue: 0,
                        options: [
                            { value: 1, label: 'Контрагенты' },
                            { value: 4, label: 'Задачи' },
                            { value: 5, label: 'Заказы' },
                            { value: 6, label: 'Диалоги' },
                        ],
                    }]}
                    value={filters}
                    onChange={(patch) => setFilters((f) => ({ ...f, ...patch }))}
                    searchValue={filters.q}
                    onSearch={(q) => setFilters((f) => ({ ...f, q }))}
                    placeholder="Поиск меток"
                />
            }
            onLoaded={onLoaded}
            extraButtons={
                <>
                    <Popconfirm
                        title={`Удалить выбранные метки (${selected.length})?`}
                        okText="Да"
                        cancelText="Нет"
                        onConfirm={removeSelected}
                    >
                        <Button danger icon={<DeleteOutlined />} disabled={!selected.length}>Удалить</Button>
                    </Popconfirm>
                    {hasNonTask
                        ? <Tooltip title="Только метки задач">{convertButton}</Tooltip>
                        : convertButton}
                </>
            }
            columns={[
                { title: 'Наименование', dataIndex: 'name' },
                { title: 'Вид', dataIndex: 'vid', render: (v) => (v === 1 ? 'Контрагенты' : v === 5 ? 'Заказы' : v === 6 ? 'Диалоги' : 'Задачи') },
                { title: 'Порядок', dataIndex: 'idx', width: 80 },
                { title: 'Превью', key: 'preview', render: (_, r) => tagPreview(r) },
            ]}
            renderForm={() => (
                <>
                    <Form.Item name="name" label="Наименование" rules={[{ required: true }]}><Input /></Form.Item>
                    <Form.Item name="vid" label="Вид" rules={[{ required: true }]} initialValue={4}>
                        <Select options={[{ value: 4, label: 'Задачи' }, { value: 1, label: 'Контрагенты' }, { value: 5, label: 'Заказы' }, { value: 6, label: 'Диалоги' }]} />
                    </Form.Item>
                    <Form.Item name="idx" label="Порядок"><InputNumber min={0} /></Form.Item>
                    <Form.Item name="textColor" label="Цвет текста" getValueFromEvent={(c) => typeof c === 'string' ? c : c?.toHexString?.() ?? ''}>
                        <ColorPicker showText />
                    </Form.Item>
                    <Form.Item name="bgColor" label="Цвет фона" getValueFromEvent={(c) => typeof c === 'string' ? c : c?.toHexString?.() ?? ''}>
                        <ColorPicker showText />
                    </Form.Item>
                </>
            )}
        />
    );
};

export default Settings;
